import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  Grid,
  Menu,
  MenuItem,
  ListItemText,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import PurchaseAnalyticsService from '../../services/purchaseAnalytics';
import { toastMessage } from '../../utils/toastMessage';
import useFullPageLoader from '../../hooks/useFullPageLoader';
import Pagination from 'react-js-pagination';
import findSerialNumber from '../../utils/findSerialNumber';
import profileImage from '../../Assests/image/profile_not_found.png';
import { dateFormat } from '../../utils/dateFormat';
import { user } from '../../routes/route';

const UsersByPurchaseCount = () => {
  const { purchaseCount } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [startDate, setStartDate] = useState(location.state?.startDate || '');
  const [endDate, setEndDate] = useState(location.state?.endDate || '');
  const [search, setSearch] = useState('');
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUserBreakdown, setSelectedUserBreakdown] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      showLoader();
      const params = {
        purchaseCount: purchaseCount,
        page: activePage - 1,
        pageSize: pageSize,
        startDate: startDate,
        endDate: endDate,
        search: search || undefined
      };
      
      const data = await PurchaseAnalyticsService.getUsersByPurchaseCount(params);
      // Backend already sorts by latest date, so no need to sort again
      setUsers(data.list || []);
      setTotal(data.pagination?.totalDocuments || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      toastMessage(error.message || 'Failed to fetch users', 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, purchaseCount]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== undefined) {
        setActivePage(1);
        fetchUsers();
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleDateFilter = () => {
    setActivePage(1);
    fetchUsers();
  };

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    setSearch('');
    setActivePage(1);
    setTimeout(() => {
      fetchUsers();
    }, 100);
  };

  const handleBreakdownClick = (event, userItem) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUserBreakdown(userItem);
  };

  const handleBreakdownClose = () => {
    setAnchorEl(null);
    setSelectedUserBreakdown(null);
  };

  if (loading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/purchase-analytics')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            {/* <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              Users with {purchaseCount} Consultation{purchaseCount !== '1' ? 's' : ''}
            </Typography> */}
          </Box>

          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M14 14L11.1 11.1" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </Box>
                      )
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      }
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: '150px' }}
                  />
                  <TextField
                    size="small"
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: '150px' }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleDateFilter} 
                    disabled={loading}
                    sx={{ minWidth: '100px' }}
                  >
                    APPLY
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleReset}
                    sx={{ minWidth: '100px' }}
                  >
                    CLEAR
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card>
            <CardContent>
              {/* <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Users List ({total} total)
              </Typography> */}
              {users.length > 0 ? (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      border: '1px solid #ddd'
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>S. No.</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Profile</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Name</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>User Id</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Phone Number</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Email</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Gender</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Registration Date</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Latest Paid chat/call Time</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Total Purchases</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Spent Amount</th>
                          {/* <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Latest Consultation Amount</th> */}
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Total Spent</th>
                          {/* <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Order ID</th> */}
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'left', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Wallet</th>
                          <th style={{ 
                            padding: '12px', 
                            border: '1px solid #ddd', 
                            textAlign: 'center', 
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((userItem, index) => (
                          <tr 
                            key={index} 
                            style={{ 
                              backgroundColor: '#fff',
                              border: '1px solid #ddd'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9f9f9'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                          >
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}>
                              {findSerialNumber(index, activePage, pageSize)}
                            </td>
                            <td className="user_profile_image" style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd'
                            }}>
                              <img
                                src={userItem.profileImage || profileImage}
                                alt={`${userItem.name} profile`}
                                style={{ 
                                  width: '50px', 
                                  height: '50px', 
                                  borderRadius: '50%', 
                                  objectFit: 'cover' 
                                }}
                              />
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}>
                              {userItem.name || '---'}
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}>
                              {userItem.userId || '---'}
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}>
                              {userItem.phone ? (
                                <a 
                                  href={`tel:${userItem.phonePrefix ? userItem.phonePrefix : ''}${userItem.phone}`} 
                                  style={{ color: '#FF8989', textDecoration: 'none' }}
                                >
                                  {userItem.phonePrefix ? `+${userItem.phonePrefix} ` : ''}{userItem.phone}
                                </a>
                              ) : (
                                '---'
                              )}
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}>
                              {userItem.email || '---'}
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}>
                              {userItem.gender || '---'}
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}>
                              {userItem.createdAt ? dateFormat(userItem.createdAt) : '---'}
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}>
                              {userItem.latestPaidChatCallTime ? dateFormat(userItem.latestPaidChatCallTime) : '---'}
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px',
                              fontWeight: 'normal'
                            }}>
                              {userItem.totalPurchases || 0}
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: '#d32f2f',
                              position: 'relative'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>
                                  {userItem.spentAmount !== undefined ? `₹${userItem.spentAmount.toFixed(2)}` : '₹0.00'}
                                </span>
                                {parseInt(purchaseCount) > 1 && userItem.purchaseBreakdown && userItem.purchaseBreakdown.length > 0 && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleBreakdownClick(e, userItem)}
                                    sx={{ 
                                      padding: '2px',
                                      color: '#1976d2',
                                      '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                                    }}
                                    title="View breakdown"
                                  >
                                    <ArrowDropDownIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </td>
                            {/* <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: '#ff9800'
                            }}>
                              {userItem.latestConsultationAmount !== undefined ? `₹${userItem.latestConsultationAmount.toFixed(2)}` : '₹0.00'}
                            </td> */}
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: '#2e7d32'
                            }}>
                              {userItem.totalSpent !== undefined ? `₹${userItem.totalSpent.toFixed(2)}` : '₹0.00'}
                            </td>
                            {/* <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px',
                              color: '#666'
                            }}>
                              {userItem.orderId || '---'}
                            </td> */}
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}>
                              {userItem.wallet ? userItem.wallet.toFixed(2) : '0.00'}
                            </td>
                            <td style={{ 
                              padding: '12px', 
                              border: '1px solid #ddd',
                              textAlign: 'center',
                              fontSize: '14px'
                            }}>
                              <Link to={`${user.USER_DETAIL}/${userItem._id}`}>
                                <IconButton 
                                  size="small" 
                                  style={{ color: '#666' }}
                                  title="View Details"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {total > pageSize && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <Pagination
                        activePage={activePage}
                        itemsCountPerPage={pageSize}
                        totalItemsCount={total}
                        pageRangeDisplayed={5}
                        onChange={(page) => setActivePage(page)}
                        itemClass="page-item"
                        linkClass="page-link"
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Alert severity="info">No users found</Alert>
              )}
            </CardContent>
          </Card>

          {/* Purchase Breakdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleBreakdownClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                maxHeight: 400,
                minWidth: 250,
                mt: 1
              }
            }}
          >
            {selectedUserBreakdown && selectedUserBreakdown.purchaseBreakdown && (
              <>
                <MenuItem disabled>
                  <ListItemText 
                    primary="Purchase Breakdown" 
                    secondary={`Total: ₹${selectedUserBreakdown.spentAmount?.toFixed(2) || '0.00'}`}
                    primaryTypographyProps={{ fontWeight: 'bold', fontSize: '14px' }}
                    secondaryTypographyProps={{ fontSize: '12px', color: '#666' }}
                  />
                </MenuItem>
                <Divider />
                {selectedUserBreakdown.purchaseBreakdown.map((purchase, index) => (
                  <MenuItem key={index} disabled>
                    <ListItemText
                      primary={`${purchase.purchaseNumber}${purchase.purchaseNumber === 1 ? 'st' : purchase.purchaseNumber === 2 ? 'nd' : purchase.purchaseNumber === 3 ? 'rd' : 'th'} Paid`}
                      secondary={`₹${purchase.amount?.toFixed(2) || '0.00'}${purchase.createdAt ? ` • ${dateFormat(purchase.createdAt)}` : ''}`}
                      primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: '11px', color: '#999' }}
                    />
                  </MenuItem>
                ))}
              </>
            )}
          </Menu>
        </Box>
      </div>
    </div>
  );
};

export default UsersByPurchaseCount;

